import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useMemo } from "react";
import {
    DocumentTaxonomyExtraction,
    FinancialDocumentListItem,
} from "@common/types/financialDocument";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { DocumentPeriodSelector } from "../../DocumentPeriodSelector/DocumentPeriodSelector";
import { formatDocumentPeriod } from "../../formatDocumentPeriod";
import { getDocumentLockedTooltip } from "../../lib";
import { useIsFinancialDocumentsTableReadonly } from "../useIsFinancialDocumentsTableReadonly";

export const PeriodCell = memo(
    ({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        FinancialDocumentListItem,
        DocumentTaxonomyExtraction | null
    >) => {
        const document = row.original;
        const extraction = document.taxonomyExtraction;
        const lockedTooltip = getDocumentLockedTooltip(
            document,
            document.integrationAccount ?? null,
        );
        const readonlyTooltip = useViewOnlyTooltipReason(lockedTooltip ?? "");
        const isTableReadonly = useIsFinancialDocumentsTableReadonly(table);
        const isReadonly = Boolean(readonlyTooltip) || isTableReadonly;

        const label = useMemo(
            () => formatDocumentPeriod(extraction),
            [extraction],
        );

        const isEmpty = !label;

        if (isReadonly) {
            return (
                <ViewTableCell captureClicks>
                    {label ? (
                        <EditableCellContent
                            readonly
                            tooltip={readonlyTooltip || undefined}
                            truncationFullText={label}
                        >
                            {label}
                        </EditableCellContent>
                    ) : null}
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks>
                <DocumentPeriodSelector financialDocument={document}>
                    <EditableCellContent
                        isEmpty={isEmpty}
                        addText="Set period"
                        truncationFullText={label ?? undefined}
                        hideIcon={!isEmpty}
                    >
                        {label}
                    </EditableCellContent>
                </DocumentPeriodSelector>
            </ViewTableCell>
        );
    },
);
PeriodCell.displayName = "PeriodCell";
